import React, { useState } from 'react';

import { Sparkline } from './charts';

/* The small pieces every view of the dashboard is made from.

   Every reading can carry the explanation of what it is, and that explanation
   is on show by default, on a phone as much as on a desk. An operator who knows
   the system switches to the compact view from the top bar: notes then step
   aside, staying in the document for screen readers, and each panel's reasoning
   moves behind an "i" button. */

export const fmt = (value, places = 1) => (
    value === null || value === undefined || Number.isNaN(value)
        ? '—'
        : !Number.isFinite(Number(value))
            ? '—'
            : Number(value).toLocaleString('en-US', { minimumFractionDigits: places, maximumFractionDigits: places })
);

const ICONS = {
    ac: 'M3 12h4l2-6 4 12 2-6h6',
    harmonics: 'M5 20V10M10 20V4M15 20v-8M20 20v-4',
    events: 'M12 7v5l3 2M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
    tests: 'M9 11l3 3 7-7M20 12v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h11',
    overview: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
    electrical: 'M13 3L5 14h6l-1 7 8-11h-6l1-7z',
    thermal: 'M14 14.5V5a2 2 0 1 0-4 0v9.5a4 4 0 1 0 4 0z',
    protection: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z',
    energy: 'M3 8h15a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM22 11v2M6 11v2M10 11v2',
    health: 'M4 5h16v6H4zM4 13h16v6H4zM8 8h.01M8 16h.01',
    library: 'M4 5a2 2 0 0 1 2-2h14v16H6a2 2 0 0 0-2 2V5z',
    menu: 'M4 7h16M4 12h16M4 17h16',
    download: 'M12 4v11M7 11l5 4 5-4M5 20h14',
    pause: 'M9 5v14M15 5v14',
    play: 'M7 5l12 7-12 7V5z',
    sliders: 'M4 7h9M17 7h3M4 17h3M11 17h9M15 5v4M9 15v4',
    exit: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
    bell: 'M6 16V11a6 6 0 1 1 12 0v5l2 2H4l2-2zM10 21h4',
    site: 'M3 12l9-8 9 8M5 10v10h14V10M10 20v-6h4v6',
    reports: 'M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6',
    simulator: 'M13 3L5 14h6l-1 7 8-11h-6l1-7zM3 5l3 3M21 5l-3 3M3 19l3-3M21 19l-3-3',
    notes: 'M4 6h16M4 12h16M4 18h9',
    printer: 'M6 9V3h12v6M6 18H4v-7h16v7h-2M6 14h12v7H6z'
};

export function Icon({ name }) {
    return (
        <svg className="dash-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d={ICONS[name]} />
        </svg>
    );
}

/** Status always arrives as words as well as color. */
export function Pill({ tone = 'muted', children }) {
    return <span className={`dash-pill dash-pill--${tone}`}>{children}</span>;
}

/* An explanation, one hover or tap away. The tap has to focus the button
   itself, because Safari on iOS does not focus a button it is tapping. */
function Info({ label, children }) {
    const [flip, setFlip] = useState(false);

    /* Opens toward whichever side has room, since the work area clips anything
       that runs past the window. */
    const measure = event => setFlip(event.currentTarget.getBoundingClientRect().left + 360 > window.innerWidth);

    return (
        <span className={`dash-info${flip ? ' dash-info--flip' : ''}`} onPointerEnter={measure} onFocus={measure}>
            <button type="button" aria-label={`About ${label}`} onClick={event => event.currentTarget.focus()}>i</button>
            <span className="dash-info__pop" role="tooltip">{children}</span>
        </span>
    );
}

/** Reasoning for a panel: its footer, or an info button in the compact view. */
export const Foot = ({ children }) => <p className="dash-note">{children}</p>;

/** A line of live summary under a table or chart, which stays on show. */
export const Caption = ({ children }) => <p className="dash-caption">{children}</p>;

export function Panel({ title, sub, badge, actions, children, id, className = '' }) {
    const parts = React.Children.toArray(children);
    const notes = parts.filter(part => part.type === Foot);
    const body = parts.filter(part => part.type !== Foot);

    return (
        <section className={`dash-panel ${className}`.trim()} id={id}>
            {(title || badge || actions) && (
                <header className="dash-panel__head">
                    <div className="dash-panel__heading">
                        {title && <h2 className="dash-panel__title">{title}</h2>}
                        {sub && <p className="dash-panel__sub">{sub}</p>}
                        {notes.length > 0 && (
                            <Info label={title}>{notes.map((note, i) => <span key={i}>{note.props.children}</span>)}</Info>
                        )}
                    </div>

                    {(badge || actions) && (
                        <div className="dash-panel__side">
                            {badge}
                            {actions}
                        </div>
                    )}
                </header>
            )}

            <div className="dash-panel__body">{body}</div>

            {notes.map((note, i) => <p className="dash-panel__foot" key={i}>{note.props.children}</p>)}
        </section>
    );
}

export function Kpi({ label, value, unit, note, tag, spark }) {
    return (
        <div className="dash-kpi">
            <span className="dash-kpi__label">{label}</span>
            <span className="dash-kpi__value">
                {value}
                {unit && <small>{unit}</small>}
            </span>
            <span className="dash-kpi__foot">
                <span>{note}</span>
                {tag}
            </span>
            {spark && <span className="dash-kpi__spark"><Sparkline values={spark} width={72} height={22} /></span>}
        </div>
    );
}

export const KpiRow = ({ children, cols = 4 }) => (
    <div className="dash-kpis" style={{ '--cols': cols }}>{children}</div>
);

/** A row of mutually exclusive buttons. */
export function Segmented({ label, options, value, onChange }) {
    return (
        <div className="dash-seg" role="group" aria-label={label}>
            {options.map(option => (
                <button
                    type="button"
                    key={option.value}
                    aria-pressed={option.value === value}
                    onClick={() => onChange(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}

export function Select({ label, value, onChange, options }) {
    return (
        <label className="dash-select">
            <span>{label}</span>
            <select value={value} onChange={event => onChange(event.target.value)}>
                {options.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
        </label>
    );
}

export const Grid = ({ children, cols = '1fr 1fr' }) => (
    <div className="dash-grid" style={{ '--grid': cols }}>{children}</div>
);

export const Note = ({ children }) => (
    <p className="dash-note">{children}</p>
);

/** A label and a reading on one line, with its explanation under the label. */
export function Row({ label, note, children }) {
    return (
        <div className="dash-row">
            <span className="dash-row__label">
                {label}
                {note && <span className="dash-row__note">{note}</span>}
            </span>
            <div className="dash-row__value">{children}</div>
        </div>
    );
}

export function Readout({ items }) {
    return (
        <dl className="dash-readout" style={{ '--count': items.length }}>
            {items.map(item => (
                <div key={item.label}>
                    <dt>{item.label}</dt>
                    <dd>{item.value}{item.unit && <small>{item.unit}</small>}</dd>
                    {item.note && <p className="dash-readout__note">{item.note}</p>}
                </div>
            ))}
        </dl>
    );
}

export function Table({ children, className = '' }) {
    return (
        <div className="dash-table-wrap">
            <table className={`dash-table ${className}`.trim()}>{children}</table>
        </div>
    );
}

/** Measurement rows side by side for the two independent buses. */
export function BusTable({ rows, scope }) {
    const showA = scope !== 'B';
    const showB = scope !== 'A';

    return (
        <Table>
            <thead>
                <tr>
                    <th scope="col">Measurement</th>
                    {showA && <th scope="col" className="is-num">Bus A</th>}
                    {showB && <th scope="col" className="is-num">Bus B</th>}
                    <th scope="col" className="is-num">Unit</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(row => (
                    <tr key={row.label}>
                        <th scope="row">
                            {row.label}
                            {row.note && <span className="dash-table__note">{row.note}</span>}
                        </th>
                        {showA && <td className="is-num">{row.a}</td>}
                        {showB && <td className="is-num">{row.b}</td>}
                        <td className="is-num is-unit">{row.unit}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
}

export const At = ({ value, time }) => (
    <>
        {value}
        <span className="dash-at">{time}</span>
    </>
);

export const BusBadge = ({ bus }) => (
    <span className={`dash-bus dash-bus--${bus.toLowerCase()}`} aria-label={`Bus ${bus}`}>{bus}</span>
);

export const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;
